import React, { useState } from "react";
import {
    Share2,
    Twitter,
    Linkedin,
    Facebook,
    Link as LinkIcon,
    Check,
    Mail,
    MessageCircle,
    Loader2,
    X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "../ui/dialog";

/**
 * ShareDialog props:
 *   open / onClose
 *   tool: "cost" | "roi"
 *   summary: payload object to persist as share
 */
export default function ShareDialog({ open, onClose, tool, summary }) {
    const [shareUrl, setShareUrl] = useState(null);
    const [imgUrl, setImgUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    React.useEffect(() => {
        if (!open || shareUrl) return;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.post(`/share/${tool}`, summary);
                const origin =
                    (typeof window !== "undefined" && window.location.origin) || "";
                setShareUrl(`${origin}/sonuc/${data.id}`);
                setImgUrl(`${origin}/api/share/${data.id}/og.png`);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const text = {
        cost: "Otel Pro ile yatırım maliyetimi hesapladım — sizinki ne çıkar?",
        roi: "Otel Pro ile yatırım geri dönüş süremi hesapladım — sizinki ne kadar?",
        staff: "Otel Pro ile personel ihtiyacımı hesapladım — sizinki ne kadar?",
        energy: "Otel Pro ile yıllık enerji maliyetini ve CO₂ ayak izini hesapladım — sizinki ne kadar?",
        occupancy: "Otel Pro ile yıllık doluluk tahminimi çıkardım — sizinki ne kadar?",
    }[tool] || "Otel Pro ile hesaplama yaptım — sen de hesapla.";

    const copy = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success("Link kopyalandı");
            setTimeout(() => setCopied(false), 1500);
        } catch (_) {
            toast.error("Kopyalanamadı");
        }
    };

    const channels = shareUrl
        ? [
              {
                  name: "X (Twitter)",
                  Icon: Twitter,
                  bg: "bg-dark-navy hover:bg-deep-blue text-white",
                  href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
              },
              {
                  name: "LinkedIn",
                  Icon: Linkedin,
                  bg: "bg-[#0a66c2] hover:bg-[#084d92] text-white",
                  href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
              },
              {
                  name: "Facebook",
                  Icon: Facebook,
                  bg: "bg-[#1877f2] hover:bg-[#0f5cc7] text-white",
                  href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
              },
              {
                  name: "WhatsApp",
                  Icon: MessageCircle,
                  bg: "bg-[#25D366] hover:bg-[#1ea854] text-white",
                  href: `https://wa.me/?text=${encodeURIComponent(text + " " + shareUrl)}`,
              },
              {
                  name: "E-posta",
                  Icon: Mail,
                  bg: "bg-deep-blue hover:bg-dark-navy text-white",
                  href: `mailto:?subject=${encodeURIComponent("Otel Pro Hesaplaması")}&body=${encodeURIComponent(text + "\n\n" + shareUrl)}`,
              },
          ]
        : [];

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg" data-testid="share-dialog">
                <DialogHeader>
                    <DialogTitle className="inline-flex items-center gap-2">
                        <Share2 className="w-5 h-5 text-teal" /> Sonucu Paylaş
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        Hesaplama sonucunuzu sosyal medya kanallarında paylaşın veya bağlantıyı kopyalayın.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    {/* Preview */}
                    <div className="rounded-lg overflow-hidden border border-slate-200 bg-light-bg" data-testid="share-preview-card">
                        {loading || !imgUrl ? (
                            <div className="aspect-[1200/630] flex items-center justify-center text-deep-blue/60 text-sm">
                                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Görsel oluşturuluyor...
                            </div>
                        ) : (
                            <img src={imgUrl} alt="Otel Pro Paylaşım Görseli" className="w-full block" />
                        )}
                    </div>

                    {/* Channels */}
                    {channels.length > 0 && (
                        <div className="grid grid-cols-5 gap-2" data-testid="share-channels">
                            {channels.map((c) => {
                                const Icon = c.Icon;
                                return (
                                    <a
                                        key={c.name}
                                        href={c.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-semibold text-[11px] ${c.bg}`}
                                        data-testid={`share-channel-${c.name}`}
                                    >
                                        <Icon className="w-4 h-4" />
                                        {c.name}
                                    </a>
                                );
                            })}
                        </div>
                    )}

                    {/* Copy link */}
                    {shareUrl && (
                        <div className="flex items-stretch rounded-md overflow-hidden border border-slate-300">
                            <input
                                value={shareUrl}
                                readOnly
                                className="flex-1 px-3 py-2.5 text-xs font-data text-deep-blue bg-light-bg outline-none"
                                data-testid="share-link-input"
                            />
                            <button
                                onClick={copy}
                                className="bg-dark-navy text-white px-4 inline-flex items-center gap-2 text-sm font-semibold"
                                data-testid="share-copy-btn"
                            >
                                {copied ? <Check className="w-4 h-4 text-data-green" /> : <LinkIcon className="w-4 h-4" />}
                                {copied ? "Kopyalandı" : "Kopyala"}
                            </button>
                        </div>
                    )}

                    <p className="text-xs text-deep-blue/60 text-center">
                        Link 1200×630 sosyal medya görseli ile birlikte açılır. Ziyaretçi kendi hesaplamasını tek tıkla yapabilir.
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
}
